import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useMemo, useState } from 'react'

import AuthService from '../../../utilities/services/auth.service'
import { isOn } from '../../../featureFlags/growthbook'

import AssignModalFormV2 from './AssignModalFormV2'

const hasStripeConnectContext = (org) =>
  Boolean(
    org?.connected_account_id ||
    org?.test_connected_account_id ||
    typeof org?.destinationAccount === 'boolean'
  )

export default function AssignTicketsModalContent({
  event,
  eventPackage,
  selectedSeatIds,
  data,
  initialAssignTo,
  initialExistingUser,
  initialPaymentMethod,
  sourceOrderId,
  addSeatsToOrderId,
  addCustomFeeToOrderId,
  sourceOrderTotal,
  sourceOrderPricingTier,
  sourceOrderCustomFeeIds,
  sourceOrderBuyerTypeId,
  reloadAssignments,
}) {
  const organizations = useMemo(() => AuthService.getOrg() || [], [])
  const organization = organizations?.[0]
  const isProd = process.env.REACT_APP_STAGE === 'production'

  // Resolve org the same way as AssignModalFormV2 so Stripe context matches where the backend creates the PI.
  // When event/package has organizationUUID but no (or minimal) organization (e.g. from OrderSummary), look up by UUID
  // so we get the correct destinationAccount and connected account for Connect (destinationAccount false).
  // Applies to both single-event assignment and season package (eventPackage) flows.
  const paymentOrgFromSession = useMemo(() => {
    const targetOrgUUID =
      event?.organizationUUID || eventPackage?.organizationUUID
    if (!targetOrgUUID || !Array.isArray(organizations)) return undefined
    return organizations.find((org) => org?.uuid === targetOrgUUID)
  }, [event?.organizationUUID, eventPackage?.organizationUUID, organizations])

  const eventOrg = event?.organization
  const packageOrg = eventPackage?.organization
  const fallbackOrg = organization
  const paymentOrg = [eventOrg, packageOrg, paymentOrgFromSession, fallbackOrg]
    .filter(Boolean)
    .reduce((selected, candidate) => {
      if (!selected) return candidate
      if (
        !hasStripeConnectContext(selected) &&
        hasStripeConnectContext(candidate)
      )
        return candidate
      return selected
    }, null)

  const connectedAccountId = isProd
    ? paymentOrg?.connected_account_id
    : paymentOrg?.test_connected_account_id

  // Validate Stripe key is present
  if (!process.env.REACT_APP_STRIPE_KEY) {
    console.error(
      'REACT_APP_STRIPE_KEY is not set. Please check your environment variables.'
    )
  }

  // Platform context (no stripeAccount) only when PIs are created on the platform: destination charges
  // (destinationAccount true), or demo/preview for orgs that are not direct Connect (destinationAccount false).
  // Direct charges (destinationAccount false) always create PIs on the connected account — demo must still use it.
  const isDemoOrPreview =
    process.env.REACT_APP_STAGE === 'demo' ||
    process.env.REACT_APP_STAGE === 'preview'
  const [stripeAccountContext, setStripeAccountContext] = useState(() =>
    paymentOrg?.destinationAccount === false
      ? connectedAccountId
      : isDemoOrPreview || paymentOrg?.destinationAccount === true
        ? null
        : connectedAccountId
  )

  const stripePromise = useMemo(
    () =>
      loadStripe(
        process.env.REACT_APP_STRIPE_KEY,
        stripeAccountContext
          ? { stripeAccount: stripeAccountContext }
          : undefined
      ),
    [stripeAccountContext]
  )

  // Must match AssignModalFormV2 `totalSeats`: count reserved + GA. Using only
  // Object.keys(selectedSeatIds)[0] undercounts when both `seats` and `gaSeats`
  // are present (wrong key order → stays "individual" → wrong group service fee).
  let selectedCount = 0
  const ids = selectedSeatIds || {}
  if (ids.seats) {
    selectedCount += Object.keys(ids.seats).length
  }
  if (ids.gaSeats) {
    Object.values(ids.gaSeats).forEach((sectionSeats) => {
      selectedCount += sectionSeats?.length || 0
    })
  }

  const typeOfTickets = selectedCount < 20 ? 'individual' : 'group'
  return (
    <Elements stripe={stripePromise}>
      <AssignModalFormV2
        ticketType={typeOfTickets}
        event={event}
        eventPackage={eventPackage}
        selectedSeatIds={selectedSeatIds}
        data={data}
        initialAssignTo={initialAssignTo}
        initialExistingUser={initialExistingUser}
        initialPaymentMethod={initialPaymentMethod}
        sourceOrderId={sourceOrderId}
        addSeatsToOrderId={addSeatsToOrderId}
        addCustomFeeToOrderId={addCustomFeeToOrderId}
        sourceOrderTotal={sourceOrderTotal}
        sourceOrderPricingTier={sourceOrderPricingTier}
        sourceOrderCustomFeeIds={sourceOrderCustomFeeIds}
        sourceOrderBuyerTypeId={sourceOrderBuyerTypeId}
        reloadAssignments={reloadAssignments}
        connectedAccountId={connectedAccountId}
        stripeAccountContext={stripeAccountContext}
        onStripeAccountContextChange={setStripeAccountContext}
      />
    </Elements>
  )
}
